import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useRestaurant } from "../contexts/RestaurantContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Play, Stop, ArrowCounterClockwise, Sun, Lightning, Moon, Users, CalendarBlank, Clock, ChartBar, ListChecks, FilePdf, CurrencyDollar } from "@phosphor-icons/react";
import { toast } from "sonner";
import PreServiceChecklist from "./PreServiceChecklist";

const STATE_CONFIG = {
  idle: { color: "#9CA3AF", bg: "#F3F4F6", icon: Sun, label: "No Active Service" },
  pre_service: { color: "#F4A261", bg: "#FEF3C7", icon: Sun, label: "Pre-Service Setup" },
  active: { color: "#81B29A", bg: "#D1FAE5", icon: Lightning, label: "Service Active" },
  post_service: { color: "#2F4F4F", bg: "#E0E7FF", icon: Moon, label: "Post-Service" },
};

export default function ServiceBar() {
  const { restId } = useRestaurant();
  const [serviceState, setServiceState] = useState("idle");
  const [session, setSession] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [showSalesPrompt, setShowSalesPrompt] = useState(false);
  const emptySalesCats = { brunch: "", lunch: "", food: "", kids_menu: "", employees_menu: "", turn_menu: "", side_orders: "", dessert: "", bev_cart_fb: "", z_pet_fb: "", wine_by_the_glass: "", wine_bottles: "", wine_bottles_white: "", rum_and_tequila: "", vodka_and_gins: "", bourbon: "", whiskey_and_scotch: "", bev_cart_liquor: "", mixed_drinks: "", beer: "", bev_cart_beer: "", soft_drinks: "", events: "" };
  const [salesCats, setSalesCats] = useState(emptySalesCats);
  const [salesNotes, setSalesNotes] = useState("");
  const [clock, setClock] = useState(new Date());
  const [occupancy, setOccupancy] = useState({ occupied: 0, total: 0 });

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const params = restId ? `?restaurant_id=${restId}` : "";
      const { data } = await api.get(`/service/status${params}`);
      setServiceState(data.state);
      setSession(data.session);
    } catch { /* silent */ }
  }, [restId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Occupancy polling during active service
  useEffect(() => {
    if (serviceState !== "active") return;
    const fetchOccupancy = async () => {
      try {
        const { data } = await api.get("/analytics/overview");
        setOccupancy({ occupied: data.occupied_tables || 0, total: data.total_tables || 0 });
      } catch { /* silent */ }
    };
    fetchOccupancy();
    const interval = setInterval(fetchOccupancy, 15000);
    return () => clearInterval(interval);
  }, [serviceState]);

  // Listen for WebSocket service updates
  useEffect(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://");
    if (!wsUrl) return;
    let ws;
    try {
      ws = new WebSocket(`${wsUrl}/api/ws`);
      ws.onmessage = (e) => {
        const msg = JSON.parse(e.data);
        if (msg.type === "service_update") fetchStatus();
      };
    } catch { /* silent */ }
    return () => ws?.close();
  }, [fetchStatus]);

  const beginPreService = async () => {
    try {
      await api.post("/service/pre-start", { restaurant_id: restId });
      fetchStatus();
      toast.success("Pre-service mode started. Assign tables & sections.");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const startService = async () => {
    try {
      await api.post("/service/start", { restaurant_id: restId });
      fetchStatus();
      toast.success("Service started!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const endService = async () => {
    try {
      const localDate = new Date().toLocaleDateString("en-CA");
      const { data } = await api.post("/service/end", {
        restaurant_id: restId,
        date: localDate,
      });
      setSession(data);
      setServiceState("post_service");
      setSalesCats(emptySalesCats);
      setSalesNotes("");
      setShowSalesPrompt(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const submitSales = async () => {
    const localDate = new Date().toLocaleDateString("en-CA");
    const catData = {};
    let hasAnySale = false;
    Object.entries(salesCats).forEach(([k, v]) => {
      const num = parseFloat(v) || 0;
      catData[k] = num;
      if (num > 0) hasAnySale = true;
    });
    if (hasAnySale) {
      try {
        await api.post("/sales", {
          restaurant_id: restId,
          date: localDate,
          ...catData,
          notes: salesNotes,
        });
        toast.success("Sales recorded");
      } catch { toast.error("Failed to save sales"); }
    }
    setShowSalesPrompt(false);
    setShowSummary(true);
  };

  const skipSales = () => {
    setShowSalesPrompt(false);
    setShowSummary(true);
  };

  const resetForNextDay = async () => {
    try {
      const localDate = new Date().toLocaleDateString("en-CA");
      await api.post("/service/reset", { restaurant_id: restId, date: localDate });
      setShowSummary(false);
      fetchStatus();
      toast.success("Reset complete. Members & server stats saved. Ready for next service.");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const cfg = STATE_CONFIG[serviceState] || STATE_CONFIG.idle;
  const Icon = cfg.icon;
  const summary = session?.summary;

  return (
    <>
      <div className="flex items-center justify-between px-4 py-2 border-b border-stone-200 text-sm" style={{ backgroundColor: cfg.bg }} data-testid="service-bar">
        <div className="flex items-center gap-2">
          <Icon size={16} weight="fill" style={{ color: cfg.color }} />
          <span className="font-semibold text-xs sm:text-sm" style={{ color: cfg.color }}>{cfg.label}</span>
          {serviceState === "active" && session?.started_at && (
            <span className="text-[11px] text-stone-500 hidden sm:inline">
              Started {new Date(session.started_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true })}
            </span>
          )}
          {serviceState === "pre_service" && (
            <span className="text-[11px] text-stone-500 hidden sm:inline">Assign tables & sections, then start service</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Live Occupancy Meter */}
          {serviceState === "active" && occupancy.total > 0 && (
            <div className="flex items-center gap-1.5" data-testid="occupancy-meter">
              <div className="relative w-8 h-8">
                <svg width="32" height="32" className="transform -rotate-90">
                  <circle cx="16" cy="16" r="13" fill="none" stroke="#E7E5E4" strokeWidth="3" />
                  <circle cx="16" cy="16" r="13" fill="none" strokeWidth="3" strokeLinecap="round"
                    stroke={occupancy.occupied / occupancy.total >= 0.85 ? "#DC2626" : occupancy.occupied / occupancy.total >= 0.6 ? "#F4A261" : "#81B29A"}
                    strokeDasharray={`${2 * Math.PI * 13}`}
                    strokeDashoffset={`${2 * Math.PI * 13 * (1 - occupancy.occupied / occupancy.total)}`}
                    style={{ transition: "stroke-dashoffset 0.8s ease, stroke 0.8s ease" }}
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold" style={{ color: occupancy.occupied / occupancy.total >= 0.85 ? "#DC2626" : "#2F4F4F" }}>
                  {Math.round((occupancy.occupied / occupancy.total) * 100)}%
                </span>
              </div>
              <span className="text-[9px] text-stone-400 hidden sm:block">{occupancy.occupied}/{occupancy.total}</span>
            </div>
          )}
          {/* Live Clock */}
          <span className="text-sm font-mono font-semibold tracking-tight" style={{ color: cfg.color }} data-testid="live-clock">
            {clock.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true })}
          </span>
          {serviceState === "idle" && (
            <Button size="sm" variant="outline" onClick={beginPreService} className="text-xs h-7" data-testid="begin-preservice-btn" style={{ borderColor: cfg.color, color: cfg.color }}>
              <Sun size={14} className="mr-1" /> Begin Pre-Service
            </Button>
          )}
          {serviceState === "pre_service" && (
            <>
              <Button size="sm" variant="outline" onClick={() => setShowChecklist(true)} className="text-xs h-7 border-white/10 text-stone-700 hover:bg-stone-100" data-testid="open-checklist-btn">
                <ListChecks size={14} className="mr-1" /> Checklist
              </Button>
              <Button size="sm" onClick={startService} className="text-xs h-7" data-testid="start-service-btn" style={{ backgroundColor: "#00FF9D", color: "#050505" }}>
                <Play size={14} className="mr-1" weight="fill" /> Start Service
              </Button>
            </>
          )}
          {serviceState === "active" && (
            <Button size="sm" variant="outline" onClick={endService} className="text-xs h-7 border-red-300 text-red-600 hover:bg-red-50" data-testid="end-service-btn">
              <Stop size={14} className="mr-1" weight="fill" /> End Service
            </Button>
          )}
          {serviceState === "post_service" && (
            <>
              <Button size="sm" variant="outline" onClick={() => setShowSummary(true)} className="text-xs h-7 border-white/10 text-stone-700 hover:bg-stone-100" data-testid="view-summary-btn">
                <ChartBar size={14} className="mr-1" /> View Summary
              </Button>
              <Button size="sm" onClick={resetForNextDay} className="text-xs h-7" data-testid="reset-service-btn" style={{ backgroundColor: "#2F4F4F" }}>
                <ArrowCounterClockwise size={14} className="mr-1" /> Reset for Next Day
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Post-Service Summary Dialog */}
      <Dialog open={showSummary} onOpenChange={setShowSummary}>
        <DialogContent className="max-w-md" data-testid="post-service-summary">
          <DialogHeader>
            <DialogTitle className="text-center" style={{ fontFamily: "Work Sans" }}>Service Summary</DialogTitle>
          </DialogHeader>
          {summary ? (
            <div className="space-y-5 mt-2">
              {/* Duration */}
              <div className="text-center p-4 rounded-xl" style={{ backgroundColor: "#2F4F4F08" }}>
                <p className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Service Duration</p>
                <p className="text-3xl font-bold mt-1" style={{ fontFamily: "Work Sans", color: "#2F4F4F" }}>
                  {summary.service_duration_minutes > 60
                    ? `${Math.floor(summary.service_duration_minutes / 60)}h ${summary.service_duration_minutes % 60}m`
                    : `${summary.service_duration_minutes}m`}
                </p>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-stone-50 text-center">
                  <Users size={18} className="mx-auto mb-1 text-stone-500" weight="duotone" />
                  <p className="text-[10px] text-stone-500 uppercase">Total Covers</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: "Work Sans", color: "#1C1917" }} data-testid="summary-covers">{summary.total_covers}</p>
                </div>
                <div className="p-3 rounded-lg bg-stone-50 text-center">
                  <CalendarBlank size={18} className="mx-auto mb-1 text-stone-500" weight="duotone" />
                  <p className="text-[10px] text-stone-500 uppercase">Reservations</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: "Work Sans", color: "#1C1917" }} data-testid="summary-reservations">{summary.total_reservations}</p>
                </div>
                <div className="p-3 rounded-lg bg-stone-50 text-center">
                  <Clock size={18} className="mx-auto mb-1 text-stone-500" weight="duotone" />
                  <p className="text-[10px] text-stone-500 uppercase">Avg Turn Time</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: "Work Sans", color: "#2F4F4F" }} data-testid="summary-avg-turn">{summary.avg_turn_minutes > 0 ? `${summary.avg_turn_minutes}m` : "--"}</p>
                </div>
                <div className="p-3 rounded-lg bg-stone-50 text-center">
                  <ChartBar size={18} className="mx-auto mb-1 text-stone-500" weight="duotone" />
                  <p className="text-[10px] text-stone-500 uppercase">Table Turns</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: "Work Sans", color: "#1C1917" }} data-testid="summary-turns">{summary.total_turns}</p>
                </div>
              </div>

              {/* Breakdown */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-1.5 border-b border-stone-100">
                  <span className="text-stone-500">Completed</span>
                  <span className="font-semibold text-stone-800">{summary.completed}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-stone-100">
                  <span className="text-stone-500">Still Seated</span>
                  <span className="font-semibold text-stone-800">{summary.seated}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-stone-100">
                  <span className="text-stone-500">Cancelled</span>
                  <span className="font-semibold text-stone-800">{summary.cancelled}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-stone-100">
                  <span className="text-stone-500">Walk-ins Seated</span>
                  <span className="font-semibold text-stone-800">{summary.walk_ins_seated}</span>
                </div>
              </div>

              <Button onClick={resetForNextDay} className="w-full" data-testid="reset-from-summary-btn" style={{ backgroundColor: "#2F4F4F" }}>
                <ArrowCounterClockwise size={16} className="mr-1.5" /> Reset for Next Day
              </Button>
              <Button
                variant="outline"
                className="w-full mt-2"
                onClick={async () => {
                  try {
                    const localDate = new Date().toLocaleDateString("en-CA");
                    const { data: recap } = await api.get(`/recap/daily?date=${localDate}`);
                    const today = new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
                    const s = summary;
                    const duration = s.service_duration_minutes > 60
                      ? `${Math.floor(s.service_duration_minutes / 60)}h ${s.service_duration_minutes % 60}m`
                      : `${s.service_duration_minutes}m`;
                    // Sales breakdown rows
                    const catLabels = { food: "Food", beer: "Beer", wine: "Wine", bourbon: "Bourbon", mixed_drinks: "Mixed Drinks", rum_and_tequila: "Rum & Tequila", vodka_and_gins: "Vodka & Gins", whiskey_and_scotch: "Whiskey & Scotch", events: "Events" };
                    const salesRows = Object.entries(recap.sales_categories || {}).map(([k, v]) =>
                      `<div class="row"><span class="row-label">${catLabels[k] || k}</span><span class="row-value">$${v.toLocaleString()}</span></div>`
                    ).join("");
                    // Server rows
                    const serverRows = (recap.server_analytics || []).map((srv) =>
                      `<div class="row"><span class="row-label"><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${srv.color};margin-right:6px"></span>${srv.server_name}</span><span class="row-value">${srv.covers} covers · ${srv.turns} turns</span></div>`
                    ).join("");
                    // Reservation detail rows
                    const resRows = (recap.reservations || []).map((r) =>
                      `<tr><td style="padding:6px 8px;border-bottom:1px solid #E7E5E4">${r.guest_name}</td><td style="padding:6px 8px;border-bottom:1px solid #E7E5E4;text-align:center">${r.party_size}</td><td style="padding:6px 8px;border-bottom:1px solid #E7E5E4">${r.time}</td><td style="padding:6px 8px;border-bottom:1px solid #E7E5E4;text-transform:capitalize">${r.type}</td><td style="padding:6px 8px;border-bottom:1px solid #E7E5E4;text-transform:capitalize">${r.status}</td></tr>`
                    ).join("");
                    const html = `
                      <html><head><title>Daily Recap - ${today}</title>
                      <style>
                        body { font-family: 'Helvetica Neue', Arial, sans-serif; max-width: 700px; margin: 30px auto; color: #1C1917; padding: 20px; font-size: 13px; }
                        h1 { font-size: 22px; color: #2F4F4F; margin-bottom: 4px; }
                        .subtitle { color: #78716C; font-size: 13px; margin-bottom: 24px; }
                        .section { margin-bottom: 20px; }
                        .section-title { font-size: 10px; text-transform: uppercase; letter-spacing: 2px; color: #78716C; font-weight: 700; margin-bottom: 8px; border-bottom: 1px solid #E7E5E4; padding-bottom: 4px; }
                        .metric-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
                        .metric { background: #F5F5F4; border-radius: 8px; padding: 14px 10px; text-align: center; }
                        .metric-value { font-size: 24px; font-weight: 700; color: #2F4F4F; }
                        .metric-label { font-size: 9px; text-transform: uppercase; letter-spacing: 1.5px; color: #78716C; margin-top: 2px; }
                        .row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #F5F5F4; font-size: 13px; }
                        .row-label { color: #78716C; display: flex; align-items: center; }
                        .row-value { font-weight: 600; }
                        .highlight { background: #2F4F4F; color: white; border-radius: 10px; padding: 14px 20px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
                        .highlight-label { font-size: 10px; text-transform: uppercase; letter-spacing: 1.5px; opacity: 0.7; }
                        .highlight-value { font-size: 22px; font-weight: 700; }
                        table { width: 100%; border-collapse: collapse; font-size: 12px; }
                        th { text-align: left; padding: 6px 8px; border-bottom: 2px solid #D6D3D1; font-size: 9px; text-transform: uppercase; letter-spacing: 1px; color: #78716C; }
                        .footer { margin-top: 28px; text-align: center; font-size: 10px; color: #A8A29E; }
                        @media print { body { margin: 10px; } }
                      </style></head><body>
                      <h1>Dining Floor Studio</h1>
                      <p class="subtitle">Daily Recap — ${today}</p>

                      ${recap.sales_total > 0 ? `<div class="highlight"><div><div class="highlight-label">Total Sales</div><div class="highlight-value">$${recap.sales_total.toLocaleString()}</div></div><div><div class="highlight-label">Rev / Cover</div><div class="highlight-value">$${recap.revenue_per_cover}</div></div><div><div class="highlight-label">Duration</div><div class="highlight-value">${duration}</div></div></div>` : ""}

                      <div class="section">
                        <div class="section-title">Key Metrics</div>
                        <div class="metric-grid">
                          <div class="metric"><div class="metric-value">${s.total_covers}</div><div class="metric-label">Total Covers</div></div>
                          <div class="metric"><div class="metric-value">${recap.total_reservations}</div><div class="metric-label">Reservations</div></div>
                          <div class="metric"><div class="metric-value">${recap.total_walkins}</div><div class="metric-label">Walk-Ins</div></div>
                          <div class="metric"><div class="metric-value">${s.avg_turn_minutes > 0 ? s.avg_turn_minutes + "m" : "--"}</div><div class="metric-label">Avg Turn</div></div>
                        </div>
                      </div>

                      ${salesRows ? `<div class="section"><div class="section-title">Sales Breakdown</div>${salesRows}<div class="row" style="font-weight:700;border-top:2px solid #D6D3D1;margin-top:4px;padding-top:8px"><span>Total</span><span>$${recap.sales_total.toLocaleString()}</span></div></div>` : ""}

                      ${serverRows ? `<div class="section"><div class="section-title">Server Performance${recap.top_server ? ` · Top: ${recap.top_server.server_name}` : ""}</div>${serverRows}</div>` : ""}

                      <div class="section">
                        <div class="section-title">Status Breakdown</div>
                        <div class="row"><span class="row-label">Completed</span><span class="row-value">${s.completed}</span></div>
                        <div class="row"><span class="row-label">Still Seated</span><span class="row-value">${s.seated}</span></div>
                        <div class="row"><span class="row-label">Cancelled</span><span class="row-value">${s.cancelled}</span></div>
                        <div class="row"><span class="row-label">Walk-ins</span><span class="row-value">${s.walk_ins_seated}</span></div>
                      </div>

                      ${resRows ? `<div class="section"><div class="section-title">Guests (${recap.reservations.length})</div><table><thead><tr><th>Name</th><th>Party</th><th>Time</th><th>Type</th><th>Status</th></tr></thead><tbody>${resRows}</tbody></table></div>` : ""}

                      <div class="footer">Generated by Dining Floor Studio · ${today}</div>
                      </body></html>`;
                    const win = window.open("", "_blank");
                    win.document.write(html);
                    win.document.close();
                    setTimeout(() => { win.print(); }, 500);
                  } catch { toast.error("Failed to generate recap"); }
                }}
                data-testid="export-pdf-btn"
              >
                <FilePdf size={16} className="mr-1.5" /> Daily Recap PDF
              </Button>
            </div>
          ) : (
            <p className="text-sm text-stone-500 text-center py-8">No summary data available.</p>
          )}
        </DialogContent>
      </Dialog>
      <PreServiceChecklist isOpen={showChecklist} onClose={() => setShowChecklist(false)} />

      {/* Sales Entry Prompt */}
      <Dialog open={showSalesPrompt} onOpenChange={setShowSalesPrompt}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="sales-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 justify-center" style={{ fontFamily: "Work Sans" }}>
              <CurrencyDollar size={20} weight="fill" style={{ color: "#2F4F4F" }} />
              Enter Today's Sales
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const v = (k) => parseFloat(salesCats[k]) || 0;
            const foodSub = v("brunch") + v("lunch") + v("food") + v("kids_menu") + v("employees_menu") + v("turn_menu") + v("side_orders") + v("dessert") + v("bev_cart_fb") + v("z_pet_fb");
            const wineSub = v("wine_by_the_glass") + v("wine_bottles") + v("wine_bottles_white");
            const liquorSub = v("rum_and_tequila") + v("vodka_and_gins") + v("bourbon") + v("whiskey_and_scotch") + v("bev_cart_liquor") + v("mixed_drinks");
            const beerSub = v("beer") + v("bev_cart_beer");
            const otherSub = v("soft_drinks") + v("events");
            const grandTotal = foodSub + wineSub + liquorSub + beerSub + otherSub;

            const CatInput = ({ k, label }) => (
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-stone-600 font-medium w-40 text-right">{label}</span>
                <div className="relative flex-1">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 text-xs">$</span>
                  <Input type="number" step="0.01" min="0" placeholder="0.00" value={salesCats[k]} onChange={(e) => setSalesCats((p) => ({ ...p, [k]: e.target.value }))} className="pl-5 h-7 text-xs" data-testid={`sales-cat-${k}`} />
                </div>
              </div>
            );

            const SubtotalRow = ({ label, value, color }) => (
              <div className="flex items-center justify-between py-1.5 px-2 rounded-md" style={{ backgroundColor: color + "12" }}>
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color }}>{label}</span>
                <span className="text-sm font-bold" style={{ color }}>${value.toFixed(2)}</span>
              </div>
            );

            return (
              <div className="space-y-3 mt-2">
                <p className="text-[10px] text-stone-400 text-center uppercase tracking-widest">Enter by category — subtotals calculate automatically</p>

                {/* Food Sales */}
                <div className="space-y-1.5">
                  <p className="text-[9px] uppercase tracking-widest text-stone-400 font-bold">Food</p>
                  <CatInput k="brunch" label="Brunch" />
                  <CatInput k="lunch" label="Lunch" />
                  <CatInput k="food" label="Dinner" />
                  <CatInput k="kids_menu" label="Kids Menu" />
                  <CatInput k="employees_menu" label="Employees Menu" />
                  <CatInput k="turn_menu" label="Turn Menu" />
                  <CatInput k="side_orders" label="Side Orders" />
                  <CatInput k="dessert" label="Dessert" />
                  <CatInput k="bev_cart_fb" label="Bev Cart F&B" />
                  <CatInput k="z_pet_fb" label="Z PE&T F&B" />
                  <SubtotalRow label="Food Sales" value={foodSub} color="#2F4F4F" />
                </div>

                {/* Wine Sales */}
                <div className="space-y-1.5">
                  <p className="text-[9px] uppercase tracking-widest text-stone-400 font-bold">Wine</p>
                  <CatInput k="wine_by_the_glass" label="Wine by the Glass" />
                  <CatInput k="wine_bottles" label="Wine Bottles - Red" />
                  <CatInput k="wine_bottles_white" label="Wine Bottles - White" />
                  <SubtotalRow label="Wine Sales" value={wineSub} color="#E07A5F" />
                </div>

                {/* Liquor Sales */}
                <div className="space-y-1.5">
                  <p className="text-[9px] uppercase tracking-widest text-stone-400 font-bold">Liquor</p>
                  <CatInput k="rum_and_tequila" label="Rum & Tequila" />
                  <CatInput k="vodka_and_gins" label="Vodka & Gins" />
                  <CatInput k="bourbon" label="Bourbon" />
                  <CatInput k="whiskey_and_scotch" label="Whiskey & Scotch" />
                  <CatInput k="bev_cart_liquor" label="Bev Cart Liquor" />
                  <CatInput k="mixed_drinks" label="Mixed Drinks" />
                  <SubtotalRow label="Liquor Sales" value={liquorSub} color="#F4A261" />
                </div>

                {/* Beer Sales */}
                <div className="space-y-1.5">
                  <p className="text-[9px] uppercase tracking-widest text-stone-400 font-bold">Beer</p>
                  <CatInput k="beer" label="Beer" />
                  <CatInput k="bev_cart_beer" label="Bev Cart Beer" />
                  <SubtotalRow label="Beer Sales" value={beerSub} color="#81B29A" />
                </div>

                {/* Other */}
                <div className="space-y-1.5">
                  <p className="text-[9px] uppercase tracking-widest text-stone-400 font-bold">Other</p>
                  <CatInput k="soft_drinks" label="Soft Drinks" />
                  <CatInput k="events" label="Events" />
                </div>

                {/* Grand Total */}
                <div className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-[#2F4F4F] text-white">
                  <span className="text-xs font-bold uppercase tracking-widest">Total Sales</span>
                  <span className="text-xl font-bold">${grandTotal.toFixed(2)}</span>
                </div>

                <Input placeholder="Notes (optional)" value={salesNotes} onChange={(e) => setSalesNotes(e.target.value)} className="text-sm" data-testid="sales-notes-input" />
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={skipSales} data-testid="skip-sales-btn">Skip</Button>
                  <Button className="flex-1" onClick={submitSales} style={{ backgroundColor: "#2F4F4F" }} data-testid="submit-sales-btn">
                    <CurrencyDollar size={16} className="mr-1" /> Save Sales
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </>
  );
}
